import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Confetti } from '../ui/Confetti';
import { playSound } from '../../utils/sound-effects';
import { useShowRewards } from '../../hooks/useShowRewards';
import { useCelebrationStore } from '../../store/celebrationStore';

/**
 * App-wide identity-title celebration (Curiosity Mission completion and future
 * milestones). Mounted once in AppShell; reveals each queued milestone with a
 * "You're a ___!" finish-line moment. Multi-day quests used to award only XP
 * and then fizzle with no live-currency payoff and no celebration.
 */
export function MilestoneModal() {
  const showRewards = useShowRewards();
  const milestone = useCelebrationStore((s) => s.milestoneQueue[0] ?? null);
  const dismiss = useCelebrationStore((s) => s.dismissCurrentMilestone);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (!milestone) return;
    const timer = setTimeout(dismiss, 4500);
    return () => clearTimeout(timer);
  }, [milestone, dismiss]);

  useEffect(() => {
    if (milestone && showRewards) {
      setShowConfetti(true);
      playSound('levelup');
      try {
        const utter = new SpeechSynthesisUtterance(`Quest complete! ${milestone.title}`);
        utter.rate = 0.95;
        window.speechSynthesis?.speak(utter);
      } catch {
        // narration is a nicety
      }
    } else {
      setShowConfetti(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [milestone?.title]);

  useEffect(() => {
    if (milestone && !showRewards) dismiss();
  }, [milestone, showRewards, dismiss]);

  if (!milestone || !showRewards) return null;

  return (
    <AnimatePresence>
      <Confetti active={showConfetti} duration={3500} particleCount={60} />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/55 flex items-center justify-center z-50 p-4"
        onClick={dismiss}
      >
        <motion.div
          initial={{ scale: 0, rotate: -8 }}
          animate={{ scale: 1, rotate: 0 }}
          exit={{ scale: 0 }}
          transition={{ type: 'spring', damping: 12, stiffness: 200 }}
          onClick={(e) => e.stopPropagation()}
          className="rounded-3xl p-8 w-full max-w-sm shadow-2xl text-center text-white"
          style={{ background: 'linear-gradient(to bottom right, #7c3aed, #4f46e5)' }}
        >
          <motion.div
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: [0, 1.3, 0.9, 1.1, 1] }}
            transition={{ delay: 0.15, duration: 0.8, times: [0, 0.3, 0.5, 0.7, 1], type: 'spring', bounce: 0.6 }}
            className="text-7xl mb-4"
          >
            {milestone.emoji}
          </motion.div>

          <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }}>
            <p className="text-lg font-medium opacity-80">Quest Complete!</p>
            <h2 className="text-2xl font-bold mb-2">{milestone.title}</h2>
            {milestone.subtitle && <p className="text-sm opacity-90">{milestone.subtitle}</p>}
          </motion.div>

          <motion.button
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            whileTap={{ scale: 0.95 }}
            onClick={dismiss}
            className="mt-6 bg-white px-8 py-3 rounded-xl font-bold text-lg text-indigo-700 hover:bg-indigo-50 transition-colors"
          >
            Yes!
          </motion.button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
